import { Box, Menu } from '@mui/material';
import {
  Archive,
  DotsThreeOutline,
  FolderPlus,
  PencilSimple,
} from '@phosphor-icons/react';
import React, { useState } from 'react';
import { SpaceMenuPopover } from '../../LayoutComponents/Spaces/SpaceMenuPopover';
import { SidebarSection } from '../shared/SidebarSection';
import {
  DeleteMenuItem,
  StyledMenuItem,
  sectionIconButtonSx,
  sectionMenuPaperSx,
} from '../sidebarSectionStyles';
import { useAgentsSidebarContext } from './context';

interface SectionProps {
  title: string;
  children: React.ReactNode;
  hasBorder?: boolean;
  isAccordion?: boolean;
  defaultExpanded?: boolean;
  isDisabled?: boolean;
  spaceId?: string | null;
  onAddFolder?: () => void;
  onArchive?: () => void;
  onEditSpace?: () => void;
  onAddUsers?: () => void;
  onCopySpaceId?: () => void;
}

export const Section: React.FC<SectionProps> = ({
  title,
  children,
  hasBorder = false,
  isAccordion = false,
  defaultExpanded = false,
  isDisabled = false,
  spaceId = null,
  onAddFolder,
  onArchive,
  onEditSpace,
  onAddUsers,
  onCopySpaceId,
}) => {
  const { isDarkMode, palette } = useAgentsSidebarContext();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const isSpaceMenu = title !== 'private';
  const hasContextActions = isSpaceMenu
    ? !!onEditSpace || !!onAddUsers || !!onCopySpaceId
    : !!onAddFolder || !!onArchive || !!onEditSpace;

  const handleClose = () => setMenuAnchor(null);
  const iconBtnSx = sectionIconButtonSx(isDarkMode, palette);

  const rightSlot = hasContextActions ? (
    <Box
      component='button'
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget as HTMLElement);
      }}
      sx={iconBtnSx}
    >
      <DotsThreeOutline
        size={16}
        weight='fill'
        color={isDarkMode ? palette.gray[400] : palette.gray[500]}
      />
    </Box>
  ) : undefined;

  const contextMenu = hasContextActions ? (
    isSpaceMenu ? (
      <SpaceMenuPopover
        key={`space-menu-${title}`}
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleClose}
        onEditSpace={onEditSpace}
        onAddUsers={onAddUsers}
        onCopySpaceId={onCopySpaceId}
      />
    ) : (
      <Menu
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: sectionMenuPaperSx(isDarkMode, palette) },
        }}
      >
        {onEditSpace && (
          <StyledMenuItem
            onClick={() => {
              handleClose();
              onEditSpace();
            }}
          >
            <PencilSimple size={16} />
            Edit Space
          </StyledMenuItem>
        )}
        {onAddFolder && (
          <StyledMenuItem
            onClick={() => {
              handleClose();
              onAddFolder();
            }}
          >
            <FolderPlus size={16} />
            Add Folder
          </StyledMenuItem>
        )}
        {onArchive && !isDisabled && (
          <DeleteMenuItem
            onClick={() => {
              handleClose();
              onArchive();
            }}
          >
            <Archive size={16} />
            Archive
          </DeleteMenuItem>
        )}
      </Menu>
    )
  ) : null;

  return (
    <SidebarSection
      title={title}
      section='agents'
      spaceId={spaceId}
      hasBorder={hasBorder}
      isAccordion={isAccordion}
      defaultExpanded={defaultExpanded}
      isDisabled={isDisabled}
      onAddFolder={onAddFolder}
      rightSlot={rightSlot}
      rightSlotForceVisible={!!menuAnchor}
      contextMenu={contextMenu}
    >
      {children}
    </SidebarSection>
  );
};
